import React, {useMemo} from "react";
import styled from "styled-components";
import Layout from "../Shared/Layout";
import {
    TableHead,
    TableRow,
    Radio,
    RadioGroup,
    FormLabel,
    FormControl,
    FormControlLabel, Container,
} from "@material-ui/core";
import { Trans } from "../utils/i18n";
import MuiButton from "@material-ui/core/Button";
import {InertiaLink, usePage} from "@inertiajs/inertia-react";
import { Field, Form as FinalForm } from "react-final-form";
import Dialog from "@mui/material/Dialog";
import { getCookieValue } from "../utils/getCookieValue";
import { Alert } from "@material-ui/lab";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeviceWidget from "../FormComponents/DeviceWidget/DeviceWidget";
import {formatCurrency} from "../utils/formatCurrency";
import {formatPercentage} from "../utils/formatPercentage";
import RadioButton from "../Shared/RadioBig";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import i18n from "i18next";
import {CheckCircle} from "@material-ui/icons";

const FieldWrapper = styled.div`
    margin-top: 10px;
    margin-bottom: 10px;
`;
const ResponseWrapper = styled.div`
    width: 100%;
    margin-top: 30px;
`;

const QuotationContainer = styled.div`
    width: 660px;
    margin: 10px auto;
    position: relative;
    color: #555555;
    background: #ffffff;
    font-size: 14px;
    font-family: "Open Sans", sans-serif;
`;

export default function OrderQuotationPage({
    order,
    quotation,
    QUOTATION_STATUS_PENDING,
    QUOTATION_STATUS_PAID,
    QUOTATION_STATUS_REJECTED,
    requestTypeName,
    secret,
    feedbacks,
    errors,
}) {
    const [showRejectionBox, setShowRejectionBox] = React.useState(false);
    const [feedbackId, setFeedbackid] = React.useState(null);
    const [rejectReason, setRejectReason] = React.useState('');
    const [rejectOption, setRejectOption] = React.useState('');

    const {
        taxAmount,
        totalAmount,
    } = useMemo(() => {
        const totalAmount = quotation.items.reduce((total, item) => {
            return total + item.quantity * item.unit_price;
        }, 0);
        const taxAmount = quotation.items.reduce((total, item) => {
            return total + ((item.quantity * item.unit_price) / (100 + item.tax / 100) * item.tax / 100);
        }, 0);
        return {
            taxAmount: taxAmount,
            totalAmount: totalAmount,
        }
    }, [quotation.items]);

    const isRejected = () => {
        return quotation?.status === QUOTATION_STATUS_REJECTED;
    };

    const isPending = () => {
        return quotation?.status === QUOTATION_STATUS_PENDING;
    };

    const selectedFeedback = feedbacks.find((option) => option.id === feedbackId);
    const formSubmittable = !!rejectOption && !!selectedFeedback && (selectedFeedback.reason_question ? !!rejectReason : true);

    const onSubmitRejectionBox = async () => {
        if (!formSubmittable) {
            return;
        }

        await fetch(route("quotation.reject", [secret]), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
                rejection_option: rejectOption,
                rejection_reason: rejectReason,
                feedback_id: feedbackId,
            }),
        })
            .then((res) => res.json())
            .then((response) => {
                if (response.success) {
                    // Rejected
                    // setShowRejectionBox(false);
                    window.location.reload();
                }
            })
            .catch(() => {
                //setFormState('error');
            });
    };

    if(!order) {
        return (
            <QuotationContainer>
                Your order quotation is not found!
            </QuotationContainer>
        )
    }

    if (quotation.status === QUOTATION_STATUS_PAID) {
        return (
            <div>
                <Container maxWidth="md">
                    <div className={'bg-blue-200 rounded-lg font-bold text-center p-1.5 text-lg mb-6 flex gap-2 justify-center items-center text-gray-900'}>
                        <CheckCircle />
                        <Trans i18nKey="orderQuotation.success.title">
                            Confirmed request
                        </Trans>
                    </div>
                    <DeviceWidget values={order.installation_address} product={order.products[0]} serviceProduct={{}} problem1={order.products[0].problem1} problem2={order.products[0].problem2} />
                    <h2 className={'mt-12'}>Updated quotation payment received</h2>
                    <div className={'flex justify-center mt-9'}>
                        <div className={'bg-blue-100 w-full max-w-sm p-6 text-center rounded-lg b2x-shadow-md'}>
                            <p>This is your repair reference number. Please keep note of this number.</p>
                            <p className={'font-bold'}>{ order.number }</p>
                        </div>
                    </div>
                    <p className={'font-bold mt-9'}>What’s next? We will proceed to repair your device</p>
                    <p>You will receive an email once the device is ready for shipment.</p>
                </Container>
            </div>
        )
    }

    if (quotation.status === QUOTATION_STATUS_REJECTED && quotation.rejection_option === 'scrap') {
        return (
            <div>
                <Container maxWidth="md">
                    <div className={'bg-blue-200 rounded-lg font-bold text-center p-1.5 text-lg mb-6 flex gap-2 justify-center items-center text-gray-900'}>
                        <CheckCircle />
                        <Trans i18nKey="orderQuotation.rejected.title">
                            Quotation rejected
                        </Trans>
                    </div>
                    <DeviceWidget values={order.installation_address} product={order.products[0]} serviceProduct={{}} problem1={order.products[0].problem1} problem2={order.products[0].problem2} />
                    <h2 className={'mt-12'}>We will recycle your device</h2>
                    <p className={'font-bold mt-9'}>Don’t worry, let us handle your device recycling process.</p>
                    <p>We will inspect it to determine if components can be recycled or reused. Whether recycled or reused, all activities relating to the processing of your device will be managed in an environmentally responsible way.</p>
                </Container>
            </div>
        )
    }

    if (quotation.status === QUOTATION_STATUS_REJECTED && quotation.rejection_option === 'return_unrepaired') {
        return (
            <div>
                <Container maxWidth="md">
                    <div className={'bg-blue-200 rounded-lg font-bold text-center p-1.5 text-lg mb-6 flex gap-2 justify-center items-center text-gray-900'}>
                        <CheckCircle />
                        <Trans i18nKey="orderQuotation.rejected.title">
                            Quotation rejected
                        </Trans>
                    </div>
                    <DeviceWidget values={order.installation_address} product={order.products[0]} serviceProduct={{}} problem1={order.products[0].problem1} problem2={order.products[0].problem2} />
                    <h2 className={'mt-12'}>We will return your device</h2>
                    <p className={'mt-9'}>
                        We are sorry to hear that you will not continue with the repair of your Amazon device with us.
                    </p>
                    <p>
                        The return of your device can be tracked <InertiaLink className={'text-gray-900 underline font-bold'} href={route('track.index', { serialnumber: order.number })}>here</InertiaLink>
                    </p>
                </Container>
            </div>
        )
    }

    return (
        <div>
            <Container maxWidth="md">
                <div className={'bg-blue-200 rounded-lg font-bold text-center p-1.5 text-lg mb-6'}>
                    <Trans i18nKey="orderQuotation.title">
                        Quotation update
                    </Trans>
                </div>
                <DeviceWidget values={order.installation_address} product={order.products[0]} serviceProduct={{}} problem1={order.products[0].problem1} problem2={order.products[0].problem2} />

                <div className={'flex gap-3 items-center justify-between mt-6'}>
                    <h1 className={'h1'}>
                        <Trans i18nKey="orderQuotation.quotationUpdate">
                            Quotation update
                        </Trans>
                    </h1>
                    <div>
                        <MuiButton
                            variant="contained"
                            color={"secondary"}
                            href={route("quotation.generate_pdf", {
                                secret,
                                filename: `quotation-${order.number}.pdf`,
                            })}
                            target="_blank"
                            style={{float: "right"}}
                            size="small"
                            endIcon={<PictureAsPdfIcon/>}
                        >
                            <Trans i18nKey="orderQuotation.downloadAs">
                                Download as
                            </Trans>
                        </MuiButton>
                    </div>
                </div>
                <p className={'font-bold'}>
                    <Trans i18nKey="orderQuotation.availableQuotation">
                        We would like to inform you about an available additional quote for your submitted repair
                    </Trans> { order.number}
                </p>
                <p className={''}>
                    <Trans i18nKey="orderQuotation.afterVerifying">
                        After verifying your device, we have detected the source of the issue indicated during the service request process. This is the additional quotation required to continue with the repair process.
                    </Trans>
                </p>
                <div className={'flex justify-center'}>
                    <div className={'b2x-shadow-md rounded-lg p-6 w-full max-w-3xl'}>
                        <div className={'overflow-auto'}>
                            <div className={'grid grid-cols-10  min-w-[600px]'}>
                                <div className={'px-3 py-1.5 col-span-2 bg-blue-50'}>
                                    <p className={'font-bold mt-0'}>Repair service:</p>
                                </div>
                                <div className={'px-3 py-1.5 col-span-2 bg-blue-100'}>
                                    <p className={'font-bold mt-0'}>Quantity:</p>
                                </div>
                                <div className={'px-3 py-1.5 col-span-2 bg-blue-50'}>
                                    <p className={'font-bold mt-0'}>Unit price:</p>
                                </div>
                                <div className={'px-3 py-1.5 col-span-2 bg-blue-100'}>
                                    <p className={'font-bold mt-0'}>Tax (%):</p>
                                </div>
                                <div className={'px-3 py-1.5 col-span-2 bg-blue-50'}>
                                    <p className={'font-bold mt-0'}>Subtotal:</p>
                                </div>

                                {quotation?.items.map((item, index) => (
                                    <>
                                        <div className={'px-3 py-1.5 col-span-2 bg-blue-50'}>
                                            { item.description }
                                        </div>
                                        <div className={'px-3 py-1.5 col-span-2 bg-blue-100'}>
                                            { item.quantity }
                                        </div>
                                        <div className={'px-3 py-1.5 col-span-2 bg-blue-50'}>
                                            { formatCurrency(item.unit_price, quotation.currency || 'GBP') }
                                        </div>
                                        <div className={'px-3 py-1.5 col-span-2 bg-blue-100'}>
                                            { formatPercentage(item.tax) }
                                        </div>
                                        <div className={'px-3 py-1.5 col-span-2 bg-blue-50'}>
                                            { formatCurrency(item.unit_price * item.quantity, quotation.currency || 'GBP') }
                                        </div>
                                    </>
                                ))}
                            </div>
                        </div>
                        <div className={'flex flex-col items-end'}>
                            <div className={'max-w-sm w-full flex gap-3 justify-between border-b border-gray-300'}>
                                <p className={'font-bold '}>Total without Taxes</p>
                                <p className={'whitespace-nowrap'}>
                                    { formatCurrency(totalAmount - taxAmount, quotation.currency || 'GBP') }
                                </p>
                            </div>
                            <div className={'max-w-sm w-full flex gap-3 justify-between border-b border-gray-600'}>
                                <p className={'font-bold '}>Taxes</p>
                                <p className={'whitespace-nowrap'}>
                                    { formatCurrency(taxAmount, quotation.currency || 'GBP') }
                                </p>
                            </div>
                            <div className={'max-w-sm w-full flex gap-3 justify-between'}>
                                <p className={'font-bold '}>Total</p>
                                <p className={'whitespace-nowrap'}>
                                    { formatCurrency(totalAmount, quotation.currency || 'GBP') }
                                </p>
                            </div>
                        </div>
                        {isPending() && (
                            <div className={'flex justify-between flex-wrap gap-3 items-center mt-6'}>
                                <MuiButton
                                    variant="outlined"
                                    color={"secondary"}
                                    onClick={() =>
                                        setShowRejectionBox(true)
                                    }
                                >
                                    <Trans i18nKey="orderQuotation.reject">
                                        Reject new quotation
                                    </Trans>
                                </MuiButton>
                                <MuiButton
                                    variant="contained"
                                    color={"secondary"}
                                    href={route(
                                        "quotation.payment.prepare_checkout",
                                        [secret]
                                    )}
                                >
                                    <Trans i18nKey="orderQuotation.payNow">
                                        Accept and pay
                                    </Trans>
                                </MuiButton>
                            </div>
                        )}
                    </div>
                </div>

                {errors &&
                    Object.keys(errors).map(function (
                        key,
                        index
                    ) {
                        return (
                            <Alert key={index} severity="error" style={{marginTop: "15px"}}>
                                {errors[key]}
                            </Alert>
                        );
                    })}

                <div style={{clear: "both"}}></div>
                <ResponseWrapper>
                    {isRejected() && (
                        <Alert
                            severity="info"
                            style={{marginBottom: "15px"}}
                        >
                            Quotation rejected with
                            <br/>
                            <strong>Option:</strong> "
                            {quotation.rejection_option}"
                            <br/>
                            <strong>Reason:</strong> "
                            {quotation.rejection_reason}"
                        </Alert>
                    )}
                </ResponseWrapper>

                <FinalForm onSubmit={onSubmitRejectionBox}>
                    {({
                          handleSubmit,
                          submitting,
                          values,
                          form,
                          errors,
                          touched,
                      }) => {
                        return (
                            <Dialog
                                fullWidth={true}
                                maxWidth="md"
                                open={showRejectionBox}
                                onClose={() => setShowRejectionBox(false)}
                                PaperProps={{
                                    className: 'rounded-lg px-16 py-12 relative',
                                }}
                            >
                                <div
                                    className={'absolute right-3 top-3 cursor-pointer text-gray-400 hover:text-gray-600'}
                                    onClick={() => {
                                        setHelpModal(false)
                                    }}>
                                    <CloseIcon/>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div>
                                        <h2>Are you sure you want to reject your new quotation?</h2>
                                        <p>If you want to reject the new quotation, select an option below for us to deal with your device and confirm the rejection:</p>
                                        <div className={'grid grid-cols-10 gap-6 mt-6'}>
                                            <div className={'col-span-10 sm:col-span-5'}>
                                                <RadioButton
                                                    input={{
                                                        checked: rejectOption === 'scrap',
                                                        value: 'scrap',
                                                        onChange: (e) => {
                                                            setRejectOption('scrap');
                                                        },
                                                    }}
                                                    field={{
                                                        titel:
                                                            <Trans i18nKey="orderQuotation.rejection_option_scrap">
                                                                Recycle
                                                            </Trans>
                                                        ,
                                                    }}
                                                    meta={{}}
                                                />
                                            </div>
                                            <div className={'col-span-10 sm:col-span-5'}>
                                                <RadioButton
                                                    input={{
                                                        checked: rejectOption === 'return_unrepaired',
                                                        value: 'return_unrepaired',
                                                        onChange: (e) => {
                                                            setRejectOption('return_unrepaired');
                                                        },
                                                    }}
                                                    field={{
                                                        titel: <Trans i18nKey="orderQuotation.rejection_option_return_unrepaired">
                                                            Return Unrepaired
                                                        </Trans>,
                                                    }}
                                                    meta={{}}
                                                />
                                            </div>
                                        </div>
                                        <p className={'mt-9'}>
                                            <Trans i18nKey="orderQuotation.recycleDisclaimer">
                                                *We can recycle any Amazon device for free. That includes batteries and electronic products as well as packaging recycling
                                            </Trans>
                                        </p>
                                        <h2 className={'mt-12'}>
                                            <Trans i18nKey="orderQuotation.reasonHeading">
                                                Why did you want to reject the quotation?
                                            </Trans>
                                        </h2>

                                        <div className={'grid grid-cols-10 gap-6 mt-6'}>
                                            {
                                                feedbacks
                                                    .map((feedback) =>
                                                        <div className={'col-span-10 sm:col-span-5'} key={feedback.id}>
                                                            <RadioButton
                                                                input={{
                                                                    checked: feedbackId === feedback.id,
                                                                    value: feedback.id,
                                                                    onChange: (e) => {
                                                                        setFeedbackid(feedback.id);
                                                                        setRejectReason('');
                                                                    },
                                                                }}
                                                                field={{
                                                                    titel: feedback.text,
                                                                }}
                                                                meta={{}}
                                                            />
                                                        </div>,
                                                    )
                                            }
                                        </div>
                                        {
                                            selectedFeedback?.reason_question &&
                                            <FieldWrapper>
                                                <p>
                                                    { selectedFeedback?.reason_question }
                                                </p>
                                                <TextField
                                                    id="rejection_reason_answer"
                                                    value={rejectReason}
                                                    onChange={(e) => {
                                                        setRejectReason(e.target.value);
                                                    }}
                                                    placeholder={
                                                        i18n.t(`common.tellUsMore`)
                                                    }
                                                    name="rejection_reason_answer"
                                                    variant="outlined"
                                                    multiline
                                                    minRows={3}
                                                />
                                            </FieldWrapper>
                                        }
                                    </div>
                                    <div className={'flex flex-wrap justify-between gap-3 w-full mt-12'}>
                                        <MuiButton
                                            variant="outlined"
                                            color={'secondary'}
                                            onClick={() =>
                                                setShowRejectionBox(false)
                                            }
                                            className={'w-full sm:w-auto'}
                                        >
                                            <Trans i18nKey="orderQuotation.cancel">
                                                Cancel
                                            </Trans>
                                        </MuiButton>
                                        <MuiButton
                                            variant="contained"
                                            color={"secondary"}
                                            type={"submit"}
                                            disabled={!formSubmittable}
                                            className={'w-full sm:w-auto'}
                                        >
                                            <Trans i18nKey="orderQuotation.reject">
                                                Reject
                                            </Trans>
                                        </MuiButton>
                                    </div>
                                </form>
                            </Dialog>
                        );
                    }}
                </FinalForm>
            </Container>
        </div>
    );
}

OrderQuotationPage.layout = (page) => <Layout children={page} />;
